import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`To install private npm packages in a Docker container, you will need to use Docker's build-time variables.`}</p>
    <h2>{`Background: runtime variables`}</h2>
    <p>{`You cannot install private npm packages in a Docker container using only runtime variables.  Consider the following Dockerfile:`}</p>
    <pre><code parentName="pre" {...{}}>{`FROM node

COPY package.json package.json  
RUN npm install

# Add your source files
COPY . .  
CMD npm start  
`}</code></pre>
    <p>{`Which will use the official `}<a parentName="p" {...{
        "href": "https://hub.docker.com/_/node"
      }}>{`Node.js`}</a>{` image, copy the `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` into our container, installs dependencies, copies the source files and runs the start command as specified in the `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`.`}</p>
    <p>{`In order to install private packages, you may think that we could just add a line before we run `}<inlineCode parentName="p">{`npm install`}</inlineCode>{`, using the `}<a parentName="p" {...{
        "href": "https://docs.docker.com/engine/reference/builder/#env"
      }}>{`ENV parameter`}</a>{`:`}</p>
    <pre><code parentName="pre" {...{}}>{`ENV NPM_TOKEN=00000000-0000-0000-0000-000000000000
`}</code></pre>
    <p>{`However, this doesn't work as you would expect, because you want the npm install to occur when you run `}<inlineCode parentName="p">{`docker build`}</inlineCode>{`, and in this instance, `}<inlineCode parentName="p">{`ENV`}</inlineCode>{` variables aren't used, they are set for runtime only.`}</p>
    <p>{`Instead of run-time variables, you must use a different way of passing environment variables to Docker, available since Docker 1.9: the `}<a parentName="p" {...{
        "href": "https://docs.docker.com/engine/reference/builder/#arg"
      }}>{`ARG parameter`}</a>{`.`}</p>
    <h2>{`Create and check in a project-specific .npmrc file`}</h2>
    <p>{`A complete example that will allow you to use `}<inlineCode parentName="p">{`--build-arg`}</inlineCode>{` to pass in your NPM_TOKEN requires adding a `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file to the project.`}</p>
    <p>{`Use a project-specific `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file with a variable for your token to securely authenticate your Docker image with npm.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`In the root directory of your project, create a custom `}<a href="https://docs.npmjs.com/cli-documentation/files/npmrc"><inlineCode parentName="p">{`.npmrc`}</inlineCode></a>{` file with the following contents:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`//registry.npmjs.org/:_authToken=\${NPM_TOKEN}
`}</code></pre>
        <p parentName="li"><strong parentName="p">{`Note:`}</strong>{` that you are specifying a literal value of `}<inlineCode parentName="p">{`\${NPM_TOKEN}`}</inlineCode>{`.  The npm cli will replace this value with the contents of the `}<inlineCode parentName="p">{`NPM_TOKEN`}</inlineCode>{` environment variable.  Do `}<strong parentName="p">{`not`}</strong>{` put a token in this file.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Check in the `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file.`}</p>
      </li>
    </ol>
    <h2>{`Update the Dockerfile`}</h2>
    <p>{`The Dockerfile that takes advantage of this has a few more lines in it than the earlier example that allows us to use the `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file and the `}<inlineCode parentName="p">{`ARG`}</inlineCode>{` parameter:`}</p>
    <pre><code parentName="pre" {...{}}>{`FROM node

ARG NPM_TOKEN  
COPY .npmrc .npmrc  
COPY package.json package.json  
RUN npm install  
RUN rm -f .npmrc

# Add your source files
COPY . .  
CMD npm start
`}</code></pre>
    <p>{`This adds the expected `}<inlineCode parentName="p">{`ARG NPM_TOKEN`}</inlineCode>{`, but also copies the `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file, and removes it when `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` completes.`}</p>
    <h2>{`Build the Docker image`}</h2>
    <p>{`To build the image using the above Dockerfile and the npm authentication token, you can run the following command. Note the `}<inlineCode parentName="p">{`.`}</inlineCode>{` at the end to give `}<inlineCode parentName="p">{`docker build`}</inlineCode>{` the current directory as an argument.`}</p>
    <pre><code parentName="pre" {...{}}>{`docker build --build-arg NPM_TOKEN=\${NPM_TOKEN} .
`}</code></pre>
    <p>{`This will build the Docker image with the current `}<inlineCode parentName="p">{`NPM_TOKEN`}</inlineCode>{` environment variable, so you can run `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` inside your container as the current logged-in user.`}</p>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note:`}</strong>{` Even if you delete the `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file, it will be kept in the commit history. To clean your secrets entirely, make sure to squash them.`}</p>
      <p><strong parentName="p">{`Note:`}</strong>{` You may commit the `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file under a different name, e.g. `}<inlineCode parentName="p">{`.npmrc.docker`}</inlineCode>{` to prevent local build from using it.`}</p>
      <p><strong parentName="p">{`Note:`}</strong>{` You may need to specify a working directory different from the default `}<inlineCode parentName="p">{`/`}</inlineCode>{`  otherwise some frameworks like Angular will fail.`}</p>
    </Note>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      